import { type MouseEvent, useCallback } from 'react';
import { setSelectedSection, setViewMode, useAppStore } from 'store/app/appStore';
import { useFavoritesStore } from 'features/favorites';
import { MULTI_VIEW_MODE, SINGLE_VIEW_MODE } from '../../../constants';
import { FAVORITE_SECTION_NAME, LOBBY_SECTION_NAME } from '@repo/common';
import type IGameSettings from 'types/models/IGameSettings';

export function useFavoriteToggle(data: IGameSettings) {
  return useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();

      // Access current state at event time (not as dependencies) to prevent re-renders
      const { viewMode, selectedSection, sections } = useAppStore.getState();
      const { toggleFavorite, debouncedRebuildFavorites, favoriteGameKeys } = useFavoritesStore.getState();
      const currentIsFavorite = favoriteGameKeys.includes(data.gameKey);

      toggleFavorite(data.gameKey, data);

      // Check edge case: removing last favorite while in favorites single-view
      const isInFavoritesSingleView = viewMode === SINGLE_VIEW_MODE && selectedSection === FAVORITE_SECTION_NAME;
      const isRemovingLastFavorite = currentIsFavorite && favoriteGameKeys.length === 1;

      // Handle edge case: auto-return to multi-view when removing last favorite in single-view
      if (isInFavoritesSingleView && isRemovingLastFavorite) {
        // Use queueMicrotask for better performance than setTimeout
        queueMicrotask(() => {
          setSelectedSection(LOBBY_SECTION_NAME);
          setViewMode(MULTI_VIEW_MODE);
        });
      }

      // PERFORMANCE OPTIMIZED: Use debounced rebuild for smooth rapid toggling
      queueMicrotask(() => {
        debouncedRebuildFavorites(sections);
      });
    },
    [data.gameKey, data], // ONLY depend on stable game data, not changing state
  );
}
